import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { IContact, IListResult, IOperatorLine, ITemplateEmail, IWorkOrderItems, TDynamicVarData } from '@core/interfaces';
import { ApiWinterService } from '@core/apis/api-winter.service';
import {UserService} from '@core/services/user.service';
import {WaycomHttpErrorResponse} from '@core/services/waycom-http-error-response';
import {ApiShivaService} from '@core/apis/api-shiva.service';
import {PromisesService} from '@core/services/promises.service';
import {ToastrService} from 'ngx-toastr';
import {TemplateEmailsModalComponent} from '@views/template-emails/template-emails-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';


@Injectable({
  providedIn: 'root'
})
export class EmailService {
  constructor(
    private apiShiva: ApiShivaService,
    private apiWinter: ApiWinterService,
    private ngbModal: NgbModal,
    private promisesService: PromisesService,
    private toastr: ToastrService,
    private userService: UserService,
  ) { }

  public getEmailVariables(
    templateEmail: string,
    emailType: string,
    woi: IWorkOrderItems | undefined,
    operatorLine: IOperatorLine | undefined
  ): string {
    if (emailType === 'intervention') {
      return this._replaceVarForIntervention(templateEmail, woi);
    } else if (emailType === 'delivery' || emailType === 'recovery') {
      return this._replaceVarForRecovery(templateEmail, woi, operatorLine);
    }
  }

  /**
   * Gets the shipping email data for the provided work order item and updates the work order item item with the obtained data.
   */
  public getShippingMailData(woi: IWorkOrderItems): Promise<void> {
    const deferred = this.promisesService.defer();

    if (!woi.id) {
      deferred.reject();
    }

    this.apiShiva.work_order_items.get_shipping_mail_data(woi.id)
      .then((res: TDynamicVarData) => {
        woi.dataForDynamicVar = res;
        deferred.resolve();
      })
      .catch(err => {
        if (err instanceof WaycomHttpErrorResponse) {
          if (err.getFirstErrorMessage() === 'EXPED_AND_EXPER_ARE_MISSING') {
            this.toastr.error(`Cette tâche est liée à une demande logistique qui ne possède ni tâche d'expédition ni tâche de retour.`);
            deferred.reject(err);
            return;
          }
        }
        this.toastr.error('Erreur lors du chargement des variables dyamiques.');
        deferred.reject(err);
      });
    return deferred.promise;
  }

  public getTemplateEmail(template_type: string): Promise<ITemplateEmail> {
    return this.apiShiva.template_emails.list({ type: template_type })
      .then((res: IListResult<ITemplateEmail>) => {
        // if only one template email we don't need to open the template modal chooser
        if (res.count === 1) {
          return res.results[0];
        } else {
          const modal = this.ngbModal.open(TemplateEmailsModalComponent, {
            backdrop: 'static',
            size: 'lg'
          });
          modal.componentInstance.disabledButtons = { create: true };
          modal.componentInstance.filters = { type: template_type };
          return modal.result;
        }
      })
      .catch(err => console.error(err));
  }

  public generateRDVDate(woi: IWorkOrderItems): string {
    if (!woi.metadata.notif_reminder_date || !woi.metadata.notif_reminder_end_date) {
      return '[[date_rendezvous]]';
    }
    const date: Date = new Date(woi.metadata.notif_reminder_date as string);
    const hour: number = date.getHours();
    const minute: string | number = date.getMinutes() < 10 ? '0' + date.getMinutes().toString() : date.getMinutes();
    const today: Date = new Date();
    const endTime: Date = new Date(today.toDateString() + ' ' + woi.metadata.notif_reminder_end_date);
    const endHour: number = endTime.getHours();
    const endMinute: string | number = endTime.getMinutes() < 10 ? '0' + endTime.getMinutes().toString() : endTime.getMinutes();
    const dateString: string = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `${dateString} de ${hour}:${minute} à ${endHour}:${endMinute}`;
  }

  private _replaceVarForIntervention(emailContent: string, woi: IWorkOrderItems): string {
    const displayDate = woi.provisional_start_date ? moment(woi.provisional_start_date).format('DD/MM/YYYY') : '';
    const displayTime = woi.provisional_start_date ? moment(woi.provisional_start_date).format('LT') : '';

    // to avoid 'undefined' display on location
    const displayLocation =
      `${woi.location?.address || ''},
      ${woi.location?.zipcode || ''}
      ${woi.location?.city || ''}`;

    // get winter href url
    const validationUrl = this.apiWinter.validateIntervention(woi.metadata?.receipt_code);

    // replace values in template with woi details
    return emailContent
      .replace('[[code_woi]]', woi.code)
      .replace('[[Nom entité]]', woi.work_order?.entity?.name || '')
      .replace('[[Ville entité]]', woi.work_order?.entity?.location?.city || '')
      .replace('[[Ref client entité]]', woi.work_order?.entity?.customer_ref || '')
      .replace('[[provisional_start_date]]', displayDate)
      .replace('[[provisional_start_time]]', displayTime)
      .replace('[[location]]', displayLocation)
      .replace('[[contact]]', this._formatContactForEmail(woi.contact))
      .replace('[[note]]', woi.note)
      .replace('[[validation_url]]', validationUrl)
      .replace('[[contractor__intervention_cost]]', woi.contractor?.intervention_cost || '')
      .replace('[[created_by]]', this.userService.getUserFullName());
  }

  /**
   * For the given emailContent, replace all dynamic variables [[var_example]]
   */
  private _replaceVarForRecovery(emailContent: string, woi: IWorkOrderItems, operatorLine: IOperatorLine | undefined): string {
    const address: string = this._generateAddress(woi);

    const displayDate: string = woi.provisional_start_date ? moment(woi.provisional_start_date).format('DD/MM/YYYY') : '';
    const displayTime: string = woi.provisional_start_date ? moment(woi.provisional_start_date).format('LT') : '';

    const displayLocation: string =
      `${woi.location?.address || ''},
      ${woi.location?.zipcode || ''}
      ${woi.location?.city || ''}`;
   const validationUrl: string = this.apiWinter.validateDelivery(woi.metadata?.receipt_code);

    if (!woi.dataForDynamicVar) {
      return emailContent;
    }

    return emailContent
      .replace('[[techno du lien]]', operatorLine?.offer?.technology?.name || '[[techno_du_lien]]')
      .replace('[[fournisseur du lien]]', operatorLine?.offer?.provider?.name || '[[fournisseur_du_lien]]')
      .replace('[[date_rendezvous]]', this.generateRDVDate(woi))
      .replace('[[nom_site]]',  woi.dataForDynamicVar['entity_name'] || '[[nom_site]]')
      .replace('[[adresse]]', `${address}`)
      .replace('[[Nom entité]]', woi.work_order?.entity?.name || '')
      .replace('[[Ville entité]]', woi.work_order?.entity?.location?.city || '')
      .replace('[[Ref client entité]]', woi.work_order?.entity?.customer_ref || '')
      .replace('[[code_woi]]', woi.code)
      .replace('[[provisional_start_date]]', displayDate)
      .replace('[[provisional_start_time]]', displayTime)
      .replace('[[location]]', displayLocation)
      .replace('[[contact]]', this._formatContactForEmail(woi.contact))
      .replace('[[note]]', woi.note)
      .replace('[[validation_url]]', validationUrl)
      .replace('[[created_by]]', this.userService.getUserFullName())
      .replace('[[date_envoi_pexped]]', woi.dataForDynamicVar['sending_woi_exped_date'] || '[[date_envoi_pexped]]')
      .replace('[[type_eqp_envoye]]', woi.dataForDynamicVar['eqp_type'] || '[[type_eqp_envoye]]')
      .replace('[[modele_eqp_envoye]]', woi.dataForDynamicVar['sending_eqp_model'] || '[[modele_eqp_envoye]]')
      .replace('[[SN_eqp_envoye]]', woi.dataForDynamicVar['sending_sn'] || '[[SN_eqp_envoye]]')
      .replace('[[type_eqp_remplacer]]', woi.dataForDynamicVar['eqp_type'] || '[[type_eqp_remplacer]]')
      .replace('[[modele_eqp_remplacer]]', woi.dataForDynamicVar['replace_eqp_modele'] || '[[modele_eqp_remplacer]]')
      .replace('[[SN_eqp_remplacer]]', woi.dataForDynamicVar['replace_sn'] || '[[sn_eqp_remplacer]]')
      .replace('[[nom_parent]]', woi.dataForDynamicVar['parent_name'] || '[[nom_parent]]')
      .replace('[[nom_site]]', woi.dataForDynamicVar['entity_name'] || '[[nom_site]]')
      .replace('[[ville]]', woi.dataForDynamicVar['city'] || '[[ville]]')
      .replace('[[shipment_ref_pexper]]', woi.dataForDynamicVar['shipment_ref_woi_exper'] || '[[shipment_ref_pexper]]')
      .replace('[[shipment_ref_pexped]]', woi.dataForDynamicVar['shipment_ref_woi_exped'] || '[[shipment_ref_pexped]]')
      .replace('[[libelle_statut_cible]]', woi.dataForDynamicVar['libelle_statut_cible'] || '[[libelle_statut_cible]]')
      .replace('[[due_date]]', woi.dataForDynamicVar['due_date'] || '[[due_date]]')
      .replace('[[due_time]]', woi.dataForDynamicVar['due_time'] || '[[due_time]]')
      .replace('[[title]]', woi.title)
      .replace('[[list_equipments]]', woi.dataForDynamicVar['list_equipments'] || '[[list_equipments]]');
  }

  private _formatContactForEmail(contact?: IContact | undefined): string {
    let contactName: string = `${contact?.first_name || ''} ${contact?.last_name || ''}`;
    if (!contactName.trim()) {
      contactName = '[[contact]]';
    }
    return contactName;
  }

  private _generateAddress(woi: IWorkOrderItems | undefined): string {
    let address = '[[adresse]]';
    const location = woi?.work_order?.entity?.location;
    if (location && location.address && location.zipcode && location.city && location.country) {
      address = `${woi.work_order.entity.location.address}, ${woi.work_order.entity.location.zipcode} ${woi.work_order.entity.location.city} ${woi.work_order.entity.location.country}`;
    }
    return address;
  }
}
